/**
 * 四柱排盘。
 *
 * 排盘在浏览器本地用 lunar-javascript 计算，与旧站一致（旧产物里同样打包了这个库），
 * 不走后端 —— 47 个 action 里没有排盘接口，后端只负责存取记录。
 *
 * 措辞注意：本页只呈现干支资料本身，不做吉凶、运势、财运一类的判断性表述。
 * 备案登记的服务类别是「文化、教育、学习资料类网站」，越界即不合规。
 */
import { useMemo, useState } from "react";
import { Solar } from "lunar-javascript";
import { legalNotice } from "../data/site";
import { callApi, getSession } from "../api";
import { chartToRecord } from "../data/record";
import { FortunePanel } from "../components/FortunePanel";

type Pillar = { label: string; gan: string; zhi: string };

const PILLAR_LABELS = ["年柱", "月柱", "日柱", "时柱"];

function splitGanZhi(value: string): [string, string] {
  return [value.slice(0, 1), value.slice(1)];
}

export function PaipanPage() {
  const [date, setDate] = useState("1990-06-15");
  const [time, setTime] = useState("14:30");
  const [gender, setGender] = useState<"male" | "female">("male");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveMessage, setSaveMessage] = useState("");
  const [submitted, setSubmitted] = useState<{ date: string; time: string } | null>(null);

  const chart = useMemo(() => {
    if (!submitted) return null;
    const [y, m, d] = submitted.date.split("-").map(Number);
    const [hh, mm] = submitted.time.split(":").map(Number);
    if (![y, m, d, hh, mm].every((n) => Number.isFinite(n))) return null;

    const solar = Solar.fromYmdHms(y, m, d, hh, mm, 0);
    const lunar = solar.getLunar();
    const eightChar = lunar.getEightChar();
    const raw = [eightChar.getYear(), eightChar.getMonth(), eightChar.getDay(), eightChar.getTime()];
    const pillars: Pillar[] = raw.map((value, index) => {
      const [gan, zhi] = splitGanZhi(value);
      return { label: PILLAR_LABELS[index], gan, zhi };
    });
    return {
      pillars,
      lunarText: `${lunar.getYearInChinese()}年 ${lunar.getMonthInChinese()}月 ${lunar.getDayInChinese()}`,
      solarText: `${y} 年 ${m} 月 ${d} 日 ${submitted.time}`,
      dayMaster: pillars[2].gan,
    };
  }, [submitted]);

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!date || !time) {
      setError("请填写完整的出生日期与时间。");
      return;
    }
    setError("");
    setSaveState("idle");
    setSaveMessage("");
    setSubmitted({ date, time });
  }

  async function saveRecord() {
    if (!chart) return;
    if (!getSession()) {
      setSaveState("error");
      setSaveMessage("记录保存在账号下，请先登录。");
      return;
    }
    setSaveState("saving");
    const record = chartToRecord(chart, { name, date, time, gender });
    const result = await callApi("records.create", { record });
    if (result.ok) {
      setSaveState("saved");
      setSaveMessage("已保存到记录案例。");
    } else {
      setSaveState("error");
      setSaveMessage(result.error);
    }
  }

  return (
    <main className="page paipan-page">
      <h1>四柱排盘</h1>
      <p className="page-summary">
        输入公历出生日期与时间，生成年、月、日、时四柱干支资料，用于学习与记录整理。
      </p>

      <form className="paipan-form" onSubmit={handleSubmit}>
        <label>
          出生日期（公历）
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
        </label>
        <label>
          出生时间
          <input type="time" value={time} onChange={(e) => setTime(e.target.value)} required />
        </label>
        <label>
          性别
          <select value={gender} onChange={(e) => setGender(e.target.value as "male" | "female")}>
            <option value="male">男</option>
            <option value="female">女</option>
          </select>
        </label>
        <button type="submit">排盘</button>
      </form>

      {error && <p className="form-error">{error}</p>}

      {chart && (
        <section className="paipan-result" aria-live="polite">
          <p className="paipan-meta">
            公历 {chart.solarText}　农历 {chart.lunarText}　日主 {chart.dayMaster}
          </p>
          <table className="pillar-table">
            <thead>
              <tr>
                {chart.pillars.map((p) => (
                  <th key={p.label} scope="col">
                    {p.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                {chart.pillars.map((p) => (
                  <td key={`${p.label}-gan`} className="pillar-gan">
                    {p.gan}
                  </td>
                ))}
              </tr>
              <tr>
                {chart.pillars.map((p) => (
                  <td key={`${p.label}-zhi`} className="pillar-zhi">
                    {p.zhi}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
          <div className="paipan-save">
            <label>
              案例名称
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="便于日后检索，可留空"
                maxLength={80}
              />
            </label>
            <button type="button" onClick={saveRecord} disabled={saveState === "saving"}>
              {saveState === "saving" ? "保存中…" : "保存到记录"}
            </button>
            {saveMessage && (
              <span className={saveState === "error" ? "form-error" : "save-ok"}>{saveMessage}</span>
            )}
          </div>

          <p className="paipan-notice">{legalNotice}</p>
        </section>
      )}

      {chart && submitted && (
        <FortunePanel date={submitted.date} time={submitted.time} gender={gender} />
      )}
    </main>
  );
}
